import React, { useState, useEffect, forwardRef, useCallback, useContext, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import styles from './AddTicketModal.module.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { parse, isValid } from 'date-fns';
import { formatDateOnly } from '../utils/dateFormatter';
import { IMaskInput } from 'react-imask';
import socketService from '../services/socket';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import '../bootstrap-icons.css';
import ProjectSelectionModal from './ProjectSelectionModal';

const API_URL = process.env.REACT_APP_API_URL || 'https://wsticket.frozentoasty.de/api';

const CustomDateInput = forwardRef((props, ref) => {
  // Destructure value and onChange from props
  const { value, onChange, onClick, onBlur, name, placeholder } = props;
  return (
    <IMaskInput
      mask="00:00:0000"
      ref={ref}
      className={styles.input + ' ' + styles.darkDatePicker}
      value={value}
      onChange={onChange}
      onClick={onClick}
      onBlur={onBlur}
      name={name}
      placeholder={placeholder || "dd:MM:yyyy"}
      autoComplete="off"
    />
  );
});

/** Auto-Grow fuer Textarea: setzt Hoehe auf scrollHeight, damit sie genau so gross ist wie der Inhalt. */
function autoGrowTextarea(e) {
  const el = e && e.target;
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = el.scrollHeight + 'px';
}

function AddTicketModal({ isOpen, onClose, onAddTicket, ticket, onDelete }) {
  const { currentUser } = useContext(AuthContext);
  const [title, setTitle] = useState(ticket?.title || '');
  const [description, setDescription] = useState(ticket?.description || '');
  const [dueDateInput, setDueDateInput] = useState(ticket?.dueDate ? formatDateInput(new Date(ticket.dueDate)) : '');
  const [project, setProject] = useState(ticket?.project || '');
  const [projectRef, setProjectRef] = useState(ticket?.projectRef?._id || '');
  const [selectedProject, setSelectedProject] = useState(ticket?.projectRef || null);
  const [taskRef, setTaskRef] = useState(ticket?.taskRef?._id || '');
  const [selectedTask, setSelectedTask] = useState(ticket?.taskRef || null);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [priority, setPriority] = useState(ticket?.priority || '');
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [stepItems, setStepItems] = useState([]);
  const [todoItems, setTodoItems] = useState([]);
  const [newStepText, setNewStepText] = useState('');
  const [newTodoText, setNewTodoText] = useState('');
  const [editingStepIdx, setEditingStepIdx] = useState(null);
  const [editingStepText, setEditingStepText] = useState('');
  const [editingTodoIdx, setEditingTodoIdx] = useState(null);
  const [editingTodoText, setEditingTodoText] = useState('');
  const [dueDate, setDueDate] = useState(ticket?.dueDate ? new Date(ticket.dueDate) : null);
  const [isMobile, setIsMobile] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const lastSaveRef = useRef(0);
  const listBusyRef = useRef(false); // serialisiert Todo/Step-PUTs (siehe runListMutation)

  const API_URL = process.env.REACT_APP_API_URL || 'https://wsticket.frozentoasty.de/api';

  const saveTicketData = useCallback(async (data) => {
    if (!ticket || !ticket._id) return;
    lastSaveRef.current = Date.now();
    try {
      await axios.put(`${API_URL}/tickets/${ticket._id}`, data);
    } catch (error) {
      console.error('Auto-save error:', error);
    }
  }, [ticket?._id]);

  // Baue combined Items {text, createdAt} aus text-array + timestamps.
  function deriveItems(texts, timestamps) {
    const t = Array.isArray(texts) ? texts : [];
    const ts = Array.isArray(timestamps) ? timestamps : [];
    return t.map((text, i) => ({
      text: typeof text === 'string' ? text : (text && text.text) || String(text ?? ''),
      createdAt: ts[i] && ts[i].createdAt ? ts[i].createdAt : null,
    }));
  }

  // Persistiert Step-Items: schickt IMMER steps + stepTimestamps zusammen (sichere Sync).
  const persistStepItems = useCallback((items) => {
    return saveTicketData({
      steps: items.map(it => it.text),
      stepTimestamps: items.map((it, i) => ({
        text: it.text,
        createdAt: it.createdAt || new Date().toISOString(),
        index: i,
      })),
    });
  }, [saveTicketData]);

  // Persistiert Todo-Items analog.
  const persistTodoItems = useCallback((items) => {
    return saveTicketData({
      todos: items.map(it => it.text),
      todoTimestamps: items.map((it, i) => ({
        text: it.text,
        createdAt: it.createdAt || new Date().toISOString(),
        index: i,
      })),
    });
  }, [saveTicketData]);

  // Persistiert Steps UND Todos zusammen in EINEM PUT.
  // Wichtig fuer Move-Operationen (Todo<->Step), da zwei separate PUTs eine
  // Race-Condition erzeugen (Mongoose .save() speichert das ganze Dokument,
  // der spaetere PUT wuerde die Aenderung des frueheren ueberschreiben).
  const persistMove = useCallback((newStepItems, newTodoItems) => {
    return saveTicketData({
      steps: newStepItems.map(it => it.text),
      stepTimestamps: newStepItems.map((it, i) => ({
        text: it.text,
        createdAt: it.createdAt || new Date().toISOString(),
        index: i,
      })),
      todos: newTodoItems.map(it => it.text),
      todoTimestamps: newTodoItems.map((it, i) => ({
        text: it.text,
        createdAt: it.createdAt || new Date().toISOString(),
        index: i,
      })),
    });
  }, [saveTicketData]);

  const debouncedSave = useMemo(() => {
    let timer;
    return (data) => {
      clearTimeout(timer);
      timer = setTimeout(() => saveTicketData(data), 800);
    };
  }, [saveTicketData]);

  // Mobile detection effect
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useEffect(() => {
    if (ticket) {
      setTitle(ticket.title || '');
      setDescription(ticket.description || '');
      setDueDate(ticket.dueDate ? new Date(ticket.dueDate) : null);
      setDueDateInput(ticket.dueDate ? formatDateInput(new Date(ticket.dueDate)) : '');
      setProject(ticket.project || '');
      setProjectRef(ticket.projectRef?._id || '');
      setSelectedProject(ticket.projectRef || null);
      setTaskRef(ticket.taskRef?._id || '');
      setSelectedTask(ticket.taskRef || null);
      setPriority(ticket.priority || '');
      setStepItems(deriveItems(ticket.steps, ticket.stepTimestamps));
      setTodoItems(deriveItems(ticket.todos, ticket.todoTimestamps));
    } else {
      setTitle('');
      setDescription('');
      setDueDate(null);
      setDueDateInput('');
      setProject('');
      setProjectRef('');
      setSelectedProject(null);
      setPriority('');
      setStepItems([]);
      setTodoItems([]);
    }
    // Nur bei Open/Ticketwechsel initialisieren: Das ticket-Objekt bekommt bei
    // jedem Socket-Refetch eine neue Identität - ein Resync dabei würde lokale
    // optimistic Updates zurückrollen (Todo doppelt/verschwunden).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, ticket?._id]);

  useEffect(() => {
    if (!ticket || !ticket._id) return;

    const handleTicketUpdated = (updatedTicket) => {
      if (!updatedTicket || updatedTicket._id !== ticket._id) return;
      if (Date.now() - lastSaveRef.current < 300) return;

      setTitle(updatedTicket.title || '');
      setDescription(updatedTicket.description || '');
      setDueDate(updatedTicket.dueDate ? new Date(updatedTicket.dueDate) : null);
      setDueDateInput(updatedTicket.dueDate ? formatDateInput(new Date(updatedTicket.dueDate)) : '');
      setProject(updatedTicket.project || '');
      setPriority(updatedTicket.priority || '');
      setStepItems(deriveItems(updatedTicket.steps, updatedTicket.stepTimestamps));
      setTodoItems(deriveItems(updatedTicket.todos, updatedTicket.todoTimestamps));

      if (updatedTicket.projectRef) {
        setProjectRef(updatedTicket.projectRef._id || updatedTicket.projectRef);
        setSelectedProject(updatedTicket.projectRef);
      } else {
        setProjectRef('');
        setSelectedProject(null);
      }
      if (updatedTicket.taskRef) {
        setTaskRef(updatedTicket.taskRef._id || updatedTicket.taskRef);
        setSelectedTask(updatedTicket.taskRef);
      } else {
        setTaskRef('');
        setSelectedTask(null);
      }
    };

    socketService.on('ticketUpdated', handleTicketUpdated);

    return () => {
      socketService.off('ticketUpdated', handleTicketUpdated);
    };
  }, [ticket?._id]);

  function formatDateInput(date) {
    if (!date || isNaN(date)) return '';
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${dd}:${mm}:${yyyy}`;
  }

  function parseDateInput(input) {
    if (!/^\d{2}:\d{2}:\d{4}$/.test(input)) return null;
    const parsed = parse(input, 'dd:MM:yyyy', new Date());
    return isValid(parsed) ? parsed : null;
  }

  // When user types in the mask
  const handleDateInputChange = (e) => {
    const val = e.target.value;
    setDueDateInput(val);
    const parsed = parseDateInput(val);
    setDueDate(parsed);
    if (parsed) saveTicketData({ dueDate: parsed.toISOString().split('T')[0] });
  };

  const handleDatePickerChange = (date) => {
    setDueDate(date);
    setDueDateInput(formatDateInput(date));
    saveTicketData({ dueDate: date ? date.toISOString().split('T')[0] : null });
  };

  const handleDeleteTicket = async () => {
    if (!ticket || !ticket._id) return;

    try {
      const response = await axios.delete(`${API_URL}/tickets/${ticket._id}`);

      if (response.status === 200) {
        console.log('Ticket deleted successfully');
        socketService.getSocket().emit('ticket-deleted', { ticketId: ticket._id });
        if (onDelete) {
          onDelete(ticket._id); // Notify parent component to refresh state
        }
        handleCloseModal(); // Close the modal after deletion
      } else {
        console.error('Failed to delete ticket:', response.data);
      }
    } catch (error) {
      console.error('Error deleting ticket:', error);
    }
  };

  const handleDeleteConfirm = () => {
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteCancel = () => {
    setIsDeleteConfirmOpen(false);
  };

  const handleDeleteConfirmAction = () => {
    handleDeleteTicket();
    setIsDeleteConfirmOpen(false);
  };

  const handleProjectSelect = (selection) => {
    const { project, task } = selection;
    setSelectedProject(project);
    setProjectRef(project?._id || '');
    setSelectedTask(task);
    setTaskRef(task?._id || '');
    saveTicketData({ projectRef: project?._id || '', taskRef: task?._id || '' });
  };

  const handleCloseModal = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 150);
  };

  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        handleCloseModal();
      }
    };
    if (isOpen) document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [isOpen]);

  if (!isOpen) return null;

  // === Step / Todo Handlers (mit combined items + persist) ===
  // listBusyRef serialisiert ALLE Todo/Step-Mutationen: Zwei schnell hintereinander
  // feuernde Handler (klassisch: onBlur-Save + Done-Klick) erzeugten sonst zwei
  // ganze-Dokument-PUTs, die sich serverseitig überschreiben -> doppelte Todos
  // (in Todos UND Steps) und verschwundene Nachbareinträge.
  const runListMutation = async (fn) => {
    if (listBusyRef.current) return;
    listBusyRef.current = true;
    try {
      await fn();
    } finally {
      listBusyRef.current = false;
    }
  };

  const handleAddStep = () => runListMutation(async () => {
    const text = newStepText.trim();
    if (!text) return;
    // Neue Steps werden vorangestellt (wie bisher)
    const updated = [{ text, createdAt: new Date().toISOString() }, ...stepItems];
    setStepItems(updated);
    setNewStepText('');
    await persistStepItems(updated);
  });

  const handleAddTodo = () => runListMutation(async () => {
    const text = newTodoText.trim();
    if (!text) return;
    const updated = [{ text, createdAt: new Date().toISOString() }, ...todoItems];
    setTodoItems(updated);
    setNewTodoText('');
    await persistTodoItems(updated);
  });

  const handleDeleteStep = (index) => runListMutation(async () => {
    const updated = stepItems.filter((_, i) => i !== index);
    setStepItems(updated);
    setEditingStepIdx(null);
    await persistStepItems(updated);
  });

  const handleDeleteTodo = (index) => runListMutation(async () => {
    const updated = todoItems.filter((_, i) => i !== index);
    setTodoItems(updated);
    setEditingTodoIdx(null);
    await persistTodoItems(updated);
  });

  const handleSaveStepEdit = () => runListMutation(async () => {
    if (editingStepIdx === null) return;
    const text = editingStepText.trim();
    if (!text) { setEditingStepIdx(null); return; }
    const updated = stepItems.map((it, i) => i === editingStepIdx ? { ...it, text } : it);
    setStepItems(updated);
    setEditingStepIdx(null);
    await persistStepItems(updated);
  });

  const handleSaveTodoEdit = () => runListMutation(async () => {
    if (editingTodoIdx === null) return;
    const text = editingTodoText.trim();
    if (!text) { setEditingTodoIdx(null); return; }
    const updated = todoItems.map((it, i) => i === editingTodoIdx ? { ...it, text } : it);
    setTodoItems(updated);
    setEditingTodoIdx(null);
    await persistTodoItems(updated);
  });

  // Todo -> Step (erledigt): createdAt bleibt erhalten, wird vor Steps gepackt.
  // WICHTIG: ein einzelner PUT mit allen 4 Feldern (sonst Race-Condition, siehe persistMove).
  // Steht das Todo gerade im Text-Edit, übernehmen wir den aktuellen Edit-Text.
  const handleMoveTodoToStep = (todoIndex) => runListMutation(async () => {
    const moved = todoItems[todoIndex];
    if (!moved) return;
    const wasEditing = editingTodoIdx === todoIndex;
    const editedText = wasEditing ? editingTodoText.trim() : '';
    setEditingTodoIdx(null);
    const newTodos = todoItems.filter((_, i) => i !== todoIndex);
    const newSteps = [
      { text: (wasEditing && editedText) ? editedText : moved.text, createdAt: moved.createdAt || new Date().toISOString() },
      ...stepItems,
    ];
    setTodoItems(newTodos);
    setStepItems(newSteps);
    await persistMove(newSteps, newTodos);
  });

  // Step -> Todo (wieder öffnen): createdAt bleibt erhalten, wird vor Todos gepackt.
  const handleMoveStepToTodo = (stepIndex) => runListMutation(async () => {
    const moved = stepItems[stepIndex];
    if (!moved) return;
    const wasEditing = editingStepIdx === stepIndex;
    const editedText = wasEditing ? editingStepText.trim() : '';
    setEditingStepIdx(null);
    const newSteps = stepItems.filter((_, i) => i !== stepIndex);
    const newTodos = [
      { text: (wasEditing && editedText) ? editedText : moved.text, createdAt: moved.createdAt || new Date().toISOString() },
      ...todoItems,
    ];
    setStepItems(newSteps);
    setTodoItems(newTodos);
    await persistMove(newSteps, newTodos);
  });

  // Kompatibilitaet: alter Name wird teilweise noch referenziert
  const handleMoveTodoToSteps = handleMoveTodoToStep;

  // Mobile navigation functions
  const nextPage = () => {
    if (currentPage < 2) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  // Touch handlers for swipe navigation
  const handleTouchStart = (e) => {
    setTouchEnd(0); // Reset touchEnd
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentPage < 2) {
      setCurrentPage(currentPage + 1);
    }
    if (isRightSwipe && currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  if (isMobile) {
    // Mobile view with 3 pages
    return (
      <div className={`${styles.modal} ${isClosing ? styles.closing : ''}`}>
        <div className={styles.mobileModalContent}>
          {/* Mobile header with navigation */}
          <div className={styles.mobileHeader}>
            <button onClick={handleCloseModal} className={styles.closeButton}>×</button>
            <h2 className={styles.mobileTitle}>{ticket ? 'Ticket bearbeiten' : 'Neues Ticket'}</h2>
            <button type="button" onClick={handleDeleteConfirm} className={styles.mobileDeleteButton}>Löschen</button>
          </div>

          {/* Page indicators */}
          <div className={styles.pageIndicators}>
            <button 
              className={`${styles.pageIndicator} ${currentPage === 0 ? styles.active : ''}`}
              onClick={() => goToPage(0)}
            >
              Details
            </button>
            <button 
              className={`${styles.pageIndicator} ${currentPage === 1 ? styles.active : ''}`}
              onClick={() => goToPage(1)}
            >
              To Do
            </button>
            <button 
              className={`${styles.pageIndicator} ${currentPage === 2 ? styles.active : ''}`}
              onClick={() => goToPage(2)}
            >
              Steps
            </button>
          </div>

          {/* Scrollable pages container */}
          <div 
            className={styles.mobilePages} 
            style={{ transform: `translateX(-${currentPage * 100}%)` }}
          >
            {/* Page 1: Details */}
            <div 
              className={styles.mobilePage}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <form onSubmit={(e) => { e.preventDefault(); handleCloseModal(); }} className={styles.mobileForm}>
                <div className={styles.mobileFormGroup}>
                  <label className={styles.mobileLabel}>Titel</label>
                  <input type="text" value={title} onChange={(e) => { setTitle(e.target.value); debouncedSave({ title: e.target.value }); }} required className={styles.mobileInput} />
                </div>
                <div className={styles.mobileFormGroup}>
                  <label className={styles.mobileLabel}>Beschreibung</label>
                  <textarea
                    value={description}
                    onChange={(e) => { setDescription(e.target.value); debouncedSave({ description: e.target.value }); }}
                    className={styles.mobileTextarea}
                  />
                </div>
                <div className={styles.mobileFormGroup}>
                  <label className={styles.mobileLabel}>Erledigen bis</label>
                  <DatePicker
                    selected={dueDate}
                    onChange={handleDatePickerChange}
                    customInput={<CustomDateInput value={dueDateInput} onChange={handleDateInputChange} />}
                    dateFormat="dd:MM:yyyy"
                    calendarClassName={styles.darkCalendar}
                    showPopperArrow={false}
                    placeholderText="dd:MM:yyyy"
                  />
                </div>
                <div className={styles.mobileFormGroup}>
                  <label className={styles.mobileLabel}>Projekt</label>
                  <div
                    className={styles.mobileInput}
                    onClick={() => setIsProjectModalOpen(true)}
                    style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: '#2a2a2a',
                      border: '1px solid #444',
                      padding: '8px'
                    }}
                  >
                    <span>
                      {project && !selectedProject ? (
                        `${project} (Legacy)`
                      ) : selectedProject ? (
                        <>
                          📁 {selectedProject.name}
                          {selectedTask && (
                            <span style={{ display: 'block', fontSize: '12px', color: '#888', marginTop: '4px' }}>
                              ✓ {selectedTask.name}
                            </span>
                          )}
                        </>
                      ) : (
                        'Kein Projekt'
                      )}
                    </span>
                    <i className="bi bi-chevron-right" style={{ color: '#888' }}></i>
                  </div>
                </div>
                <div className={styles.mobileFormGroup}>
                  <label className={styles.mobileLabel}>Priorität</label>
                  <select value={priority} onChange={(e) => { setPriority(e.target.value); saveTicketData({ priority: e.target.value }); }} className={styles.mobileSelect}>
                    <option value="">Priorität auswählen</option>
                    <option value="Low">Niedrig</option>
                    <option value="Medium">Mittel</option>
                    <option value="High">Hoch</option>
                  </select>
                </div>
              </form>
            </div>

            {/* Page 2: To Do */}
            <div 
              className={styles.mobilePage}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className={styles.mobilePageContent}>
                <h3 className={styles.mobilePageTitle}>To Do</h3>
                <input
                  type="text"
                  placeholder="Add a to do"
                  value={newTodoText}
                  onChange={(e) => setNewTodoText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTodo(); } }}
                  className={styles.mobileInput}
                />
                <div className={styles.mobileList}>
                  {todoItems.map((item, index) => (
                    <div key={index} className={styles.mobileListItem}>
                      {editingTodoIdx === index ? (
                        <textarea
                          className={styles.itemEditTextarea}
                          style={{ flex: 1 }}
                          value={editingTodoText}
                          autoFocus
                          onFocus={autoGrowTextarea}
                          onChange={(e) => { setEditingTodoText(e.target.value); autoGrowTextarea(e); }}
                          onBlur={handleSaveTodoEdit}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveTodoEdit(); }
                            if (e.key === 'Escape') setEditingTodoIdx(null);
                          }}
                        />
                      ) : (
                        <span
                          className={styles.mobileListText}
                          title="Doppelklick zum Bearbeiten"
                          onDoubleClick={() => { setEditingTodoIdx(index); setEditingTodoText(item.text); }}
                        >{item.text}</span>
                      )}
                      <div className={styles.mobileListActions} onMouseDown={(e) => e.preventDefault()}>
                        <button
                          onClick={() => handleMoveTodoToStep(index)}
                          className={styles.mobileDoneButton}
                          title="Als erledigt markieren (→ Step)"
                        >✓</button>
                        <button
                          onClick={() => handleDeleteTodo(index)}
                          className={styles.mobileDeleteItemButton}
                          title="Löschen"
                        ><i className="bi bi-trash3"></i></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Page 3: Steps */}
            <div 
              className={styles.mobilePage}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className={styles.mobilePageContent}>
                <h3 className={styles.mobilePageTitle}>Steps</h3>
                <input
                  type="text"
                  placeholder="Add a step"
                  value={newStepText}
                  onChange={(e) => setNewStepText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddStep(); } }}
                  className={styles.mobileInput}
                />
                <div className={styles.mobileList}>
                  {stepItems.map((item, index) => (
                    <div key={index} className={styles.mobileListItem}>
                      {editingStepIdx === index ? (
                        <textarea
                          className={styles.itemEditTextarea}
                          style={{ flex: 1 }}
                          value={editingStepText}
                          autoFocus
                          onFocus={autoGrowTextarea}
                          onChange={(e) => { setEditingStepText(e.target.value); autoGrowTextarea(e); }}
                          onBlur={handleSaveStepEdit}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveStepEdit(); }
                            if (e.key === 'Escape') setEditingStepIdx(null);
                          }}
                        />
                      ) : (
                        <span
                          className={styles.mobileListText}
                          title="Doppelklick zum Bearbeiten"
                          onDoubleClick={() => { setEditingStepIdx(index); setEditingStepText(item.text); }}
                        >{item.text}</span>
                      )}
                      <div className={styles.mobileListActions} onMouseDown={(e) => e.preventDefault()}>
                        <button
                          onClick={() => handleMoveStepToTodo(index)}
                          className={styles.mobileDoneButton}
                          title="Wieder öffnen (→ Todo)"
                        >↩</button>
                        <button
                          onClick={() => handleDeleteStep(index)}
                          className={styles.mobileDeleteItemButton}
                          title="Löschen"
                        ><i className="bi bi-trash3"></i></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Save button at bottom */}
          <div className={styles.mobileFooter}>
            <button onClick={handleCloseModal} className={styles.mobileSaveButton}>Schliessen</button>
          </div>
        </div>
      </div>
    );
  }

  // Desktop view (unchanged)
  return (
    <div className={`${styles.modal} ${isClosing ? styles.closing : ''}`}>
      <div className={styles.modalContent} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', width: '1400px', height: '750px', alignItems: 'stretch', padding: '20px 20px 15px 20px', transform: 'inherit', opacity: 'inherit' }}>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Left side: Ticket details */}
          <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>{ticket ? 'Ticket Bearbeiten' : 'Ticket Hinzufügen'}</h2>
          <form onSubmit={(e) => { e.preventDefault(); handleCloseModal(); }}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Titel</label>
              <input type="text" value={title} onChange={(e) => {
                const newValue = e.target.value;
                setTitle(newValue);
                debouncedSave({ title: newValue });
              }} required className={styles.input} />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Beschreibung</label>
              <textarea
                value={description}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setDescription(newValue);
                  debouncedSave({ description: newValue });
                }}
                className={styles.textarea}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Erledigen bis</label>
              <DatePicker
                selected={dueDate}
                onChange={handleDatePickerChange}
                customInput={
                  <CustomDateInput
                    value={dueDateInput}
                    onChange={handleDateInputChange}
                  />
                }
                dateFormat="dd:MM:yyyy"
                calendarClassName={styles.darkCalendar}
                showPopperArrow={false}
                placeholderText="dd:MM:yyyy"
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Projekt</label>
              <div
                className={styles.input}
                onClick={() => setIsProjectModalOpen(true)}
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: '#2a2a2a',
                  border: '1px solid #444',
                  padding: '8px'
                }}
              >
                <span>
                  {project && !selectedProject ? (
                    `${project} (Legacy)`
                  ) : selectedProject ? (
                    <>
                      📁 {selectedProject.name}
                      {selectedTask && (
                        <span style={{ display: 'block', fontSize: '12px', color: '#888', marginTop: '4px' }}>
                          ✓ {selectedTask.name}
                        </span>
                      )}
                    </>
                  ) : (
                    'Kein Projekt'
                  )}
                </span>
                <i className="bi bi-chevron-right" style={{ color: '#888' }}></i>
              </div>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Priorität</label>
              <select value={priority} onChange={(e) => {
                const newValue = e.target.value;
                setPriority(newValue);
                saveTicketData({ priority: newValue });
              }} className={styles.select}>
                <option value="">Priorität auswählen</option>
                <option value="Low">Niedrig</option>
                <option value="Medium">Mittel</option>
                <option value="High">Hoch</option>
              </select>
            </div>
            
            <div className={styles.buttonGroup}>
              <button type="submit" className={styles.primaryButton}>{ticket ? 'Schliessen' : 'Ticket Hinzufügen'}</button>
              <button type="button" onClick={handleDeleteConfirm} className={styles.deleteButton}>Ticket Löschen</button>
            </div>
          </form>
        </div>
        <div className={styles.stepsContainer}>
          <h3>To Do {todoItems.length > 0 && <span className={styles.itemCount}>{todoItems.length}</span>}</h3>
          <input
            type="text"
            placeholder="Add a to do"
            value={newTodoText}
            onChange={(e) => setNewTodoText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTodo(); } }}
            className={styles.stepsInput}
          />
          <div className={styles.stepsTableWrapper}>
            <div className={styles.itemList}>
              {todoItems.length === 0 && <p className={styles.itemEmpty}>Keine Todos.</p>}
              {todoItems.map((item, index) => (
                <div key={index} className={styles.itemRow}>
                  <div className={styles.itemMain}>
                    {editingTodoIdx === index ? (
                      <textarea
                        className={styles.itemEditTextarea}
                        value={editingTodoText}
                        autoFocus
                        onFocus={autoGrowTextarea}
                        onChange={(e) => { setEditingTodoText(e.target.value); autoGrowTextarea(e); }}
                        onBlur={handleSaveTodoEdit}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveTodoEdit(); }
                          if (e.key === 'Escape') setEditingTodoIdx(null);
                        }}
                      />
                    ) : (
                      <span
                        className={styles.itemText}
                        title="Doppelklick zum Bearbeiten"
                        onDoubleClick={() => { setEditingTodoIdx(index); setEditingTodoText(item.text); }}
                      >{item.text}</span>
                    )}
                    {item.createdAt && (
                      <span className={styles.itemDate}>{formatDateOnly(item.createdAt)}</span>
                    )}
                  </div>
                  <div className={styles.itemActions} onMouseDown={(e) => e.preventDefault()}>
                    <button
                      className={styles.itemActionBtn}
                      onClick={() => handleMoveTodoToStep(index)}
                      title="Als erledigt markieren (→ Step)"
                    ><i className="bi bi-check2"></i></button>
                    <button
                      className={`${styles.itemActionBtn} ${styles.itemActionDanger}`}
                      onClick={() => handleDeleteTodo(index)}
                      title="Löschen"
                    ><i className="bi bi-trash3"></i></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className={styles.stepsContainer}>
          <h3>Steps {stepItems.length > 0 && <span className={styles.itemCount}>{stepItems.length}</span>}</h3>
          <input
            type="text"
            placeholder="Add a step"
            value={newStepText}
            onChange={(e) => setNewStepText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddStep(); } }}
            className={styles.stepsInput}
          />
          <div className={styles.stepsTableWrapper}>
            <div className={styles.itemList}>
              {stepItems.length === 0 && <p className={styles.itemEmpty}>Keine Steps.</p>}
              {stepItems.map((item, index) => (
                <div key={index} className={styles.itemRow}>
                  <div className={styles.itemMain}>
                    {editingStepIdx === index ? (
                      <textarea
                        className={styles.itemEditTextarea}
                        value={editingStepText}
                        autoFocus
                        onFocus={autoGrowTextarea}
                        onChange={(e) => { setEditingStepText(e.target.value); autoGrowTextarea(e); }}
                        onBlur={handleSaveStepEdit}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveStepEdit(); }
                          if (e.key === 'Escape') setEditingStepIdx(null);
                        }}
                      />
                    ) : (
                      <span
                        className={styles.itemText}
                        title="Doppelklick zum Bearbeiten"
                        onDoubleClick={() => { setEditingStepIdx(index); setEditingStepText(item.text); }}
                      >{item.text}</span>
                    )}
                    {item.createdAt && (
                      <span className={styles.itemDate}>{formatDateOnly(item.createdAt)}</span>
                    )}
                  </div>
                  <div className={styles.itemActions} onMouseDown={(e) => e.preventDefault()}>
                    <button
                      className={styles.itemActionBtn}
                      onClick={() => handleMoveStepToTodo(index)}
                      title="Wieder öffnen (→ Todo)"
                    ><i className="bi bi-arrow-counterclockwise"></i></button>
                    <button
                      className={`${styles.itemActionBtn} ${styles.itemActionDanger}`}
                      onClick={() => handleDeleteStep(index)}
                      title="Löschen"
                    ><i className="bi bi-trash3"></i></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {isDeleteConfirmOpen && (
        ReactDOM.createPortal(
          <div className={styles.modalOverlay} style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}>
            <div className={styles.modalContent} style={{ padding: '15px', width: '400px' }}>
              <h2 style={{ fontSize: '1.5em', marginBottom: '10px', textAlign: 'center' }}>Delete Ticket</h2>
              <p style={{ fontSize: '1em', marginBottom: '15px', textAlign: 'center' }}>
                Are you sure you want to delete this ticket? <br />
                This action cannot be undone.
              </p>
              <div className={styles.buttonGroup} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                <button className={styles.secondaryButton} style={{ padding: '8px 12px' }} onClick={handleDeleteCancel}>
                  Cancel
                </button>
                <button
                  className={styles.primaryButton}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: 'red',
                    color: 'white',
                    transition: 'background-color 0.3s ease',
                  }}
                  onClick={handleDeleteConfirmAction}
                  onMouseEnter={(e) => (e.target.style.backgroundColor = '#cc0000')}
                  onMouseLeave={(e) => (e.target.style.backgroundColor = 'red')}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      )}

      {/* Project Selection Modal */}
      <ProjectSelectionModal
        isOpen={isProjectModalOpen}
        onClose={() => setIsProjectModalOpen(false)}
        onSelectProject={handleProjectSelect}
        currentProject={selectedProject}
        currentTask={selectedTask}
      />
    </div>
  );
}

export default AddTicketModal;
